import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface AdditionalActState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  companyId: string;
  offerId: string;
  enumContractStatusId: any;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  datePeriod: any;
  acquisitionType: any;
  isExpedite: any;
  hasAutomaticExtension: any;
  intervention: any;
  hasUnlimitedInterventions: any;
  haveFile: boolean;
  file: string;
  registrationNumber: string;
  remarks: string;
  metadata: any;
  company: any;
  accountManager: any;
  accountSale: any;
  enumContractStatus: any;
  contractWorkPlaceItems: any[];
  contractLaborExtras: any[];
  contractCommentsTab: any[];
  location: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: AdditionalActState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  companyId: '',
  offerId: '',
  enumContractStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  datePeriod: 30,
  acquisitionType: '',
  isExpedite: false,
  hasAutomaticExtension: false,
  intervention: 3,
  hasUnlimitedInterventions: false,
  haveFile: false,
  file: '',
  registrationNumber: '',
  remarks: '',
  metadata: null,
  company: null,
  accountManager: null,
  accountSale: null,
  enumContractStatus: null,
  contractWorkPlaceItems: [],
  contractLaborExtras: [],
  contractCommentsTab: [],
  location: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300, interventionPerSystemType: 1, interventionPriceOnRequest: 300 },
};

const additionalActSlice = createSlice({
  name: 'additionalAct',
  initialState,
  reducers: {
    setAdditional: (state, action: PayloadAction<Partial<AdditionalActState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addAdditionalWorkPlaceItem: (state, action) => {
      state.contractWorkPlaceItems.push(action.payload);
    },
    updateAdditionalWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.contractWorkPlaceItems[index]) {
        state.contractWorkPlaceItems[index] = {
          ...state.contractWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removeAdditionalWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.contractWorkPlaceItems.length) {
        state.contractWorkPlaceItems.splice(index, 1);
      }
    },
    addAdditionalLaborExtra: (state, action) => {
      state.contractLaborExtras.push(action.payload);
    },
    addAdditionalComment: (state, action: PayloadAction<any>) => {
      state.contractCommentsTab.push(action.payload);
    },
    updateAdditionalComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.contractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        // Update existing comment
        state.contractCommentsTab[index] = {
          ...state.contractCommentsTab[index],
          ...updates,
        };
      }
    },
    // Remove a file from a comment
    removeAdditionalComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.contractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (commentIndex !== -1) {
        const comment = state.contractCommentsTab[commentIndex];

        // Filter out the file
        comment.contractCommentFiles = comment.contractCommentFiles.filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        // Update the comment in the array
        state.contractCommentsTab[commentIndex] = {
          ...comment,
          contractCommentFiles: comment.contractCommentFiles,
        };
      }
    },
  },
});

export const {
  setAdditional,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addAdditionalWorkPlaceItem,
  updateAdditionalWorkPlaceItem,
  removeAdditionalWorkPlaceItem,
  addAdditionalLaborExtra,
  addAdditionalComment,
  updateAdditionalComment,
  removeAdditionalComment,
} = additionalActSlice.actions;

export default additionalActSlice.reducer;
